import React from 'react';
import isNil from 'lodash.isnil';
import isEmpty from 'lodash.isempty';
import get from 'lodash.get';
import { Link } from 'react-router-dom';
import { getStudentResultByMeasureYearGradePeriod } from '../../../utils/shared/filterUtils';
import { getNavigationUrl, getUrlParams } from '../../../utils/shared/urlUtils';
import CompositeCell from '../../../components/cell/shared/CompositeCell';
import {
    GradeNameCell,
    StudentNameCell,
    TextCell,
    TwoPartHeaderCell,
} from '../../../components/cell/shared/Cells';
import { getGradeTitle } from '../../../constants/shared/GradesConstants';
import {
    GA,
    getPmPeriodByCurrentPeriod,
    GRID,
    HOW_TO_UNDERSTAND,
    PERIOD,
    ZOG,
} from '../../../constants/shared/CommonConstants';
import { INDEX, PERIODS_FOR_ZOG } from '../../../constants/shared/ZoGConstants';
import { buildColSpan } from '../../../utils/shared/tableUtils';
import {
    CELL_TYPE,
    CLASS_NAME,
    INDEX as CELL_INDEX,
} from '../../../constants/shared/CellConstants'; // LEFT_ALIGN_CLASS_NAME, // FIRST_ARRAY_INDEX, // BENCHMARK_GATING_MEASURE_CELL_TYPE, // BENCHMARK_CELL_TYPE,
import {
    HELP_PANEL_TAB,
    PM_COL_SPAN_SUBHEADER_CELL,
    PM_PERIODS,
    PM_ROW_SPAN,
    TITLE,
} from '../../../constants/shared/PMConstants';
import { PROG_ZOG_TABLE } from '../../../constants/reading/DemoModeConstants';
import { getLegendPanelTitle } from '../../legend/shared/legendUtils';
import {
    getGrowthAnalysisPeriodByCurrentPeriod,
    PERIODS_FOR_GROWTH_ANALYSIS,
} from '../../../constants/shared/GrowthAnalysisConstants';
import { generateReactKey } from '../../../utils/shared/commonUtils';

const { COL_SPAN_WITH_HEIGHT_STYLE, COMMON_HEADER_COL_SPAN_STYLE } = GRID;

const { BOY_MOY, MOY_EOY } = PERIOD;
const { GOAL, PROGRESS, GROWTH_ANALYSIS } = HOW_TO_UNDERSTAND;
const { PM_TITLE } = TITLE;

export const getCommonGradeHeaderCell = grade => ({
    name: <GradeNameCell name={getGradeTitle(grade)} showSortingIcon />,
});

export const getTwoPartHeaderCell = (
    key,
    className,
    spanClassNames,
    firstPartTitle,
    secondPartTitle,
    showSortingIcon,
    sortDesc,
) => ({
    [key]: (
        <TwoPartHeaderCell
            classNames={`subheader-cell ${className}`}
            spanClassNames={spanClassNames}
            firstPartTitle={firstPartTitle}
            secondPartTitle={secondPartTitle}
            showSortingIcon={showSortingIcon}
            sortDesc={sortDesc}
        />
    ),
});

export const buildDividedColSpan = (title, sections) => (
    <TextCell
        classNames={PM_COL_SPAN_SUBHEADER_CELL}
        content={
            <>
                <div className="divider-title">{title}</div>
                <div className="divider-container">
                    {sections.map((name, index) => (
                        <div
                            key={generateReactKey(name)}
                            className={`divider-section ${
                                index === CELL_INDEX.FIRST_ARRAY_INDEX ? 'first-section' : ''
                            }`}
                        >
                            {name}
                        </div>
                    ))}
                </div>
            </>
        }
        isDataCell={false}
    />
);

export const getDividedColSpan = (resultsByGrades, columnStartIndex, columnStopIndex, child) => {
    let rowIndex = INDEX.ROW_START_INDEX;
    return resultsByGrades.map(({ studentsData }) => {
        const colSpan = buildColSpan(
            rowIndex,
            {
                columnStartIndex,
                columnStopIndex,
            },
            child,
            COL_SPAN_WITH_HEIGHT_STYLE,
        );
        studentsData.forEach(student => {
            const resultsLength = student.data.length;
            rowIndex += resultsLength || 1;
        });
        rowIndex += 1;
        return colSpan;
    });
};

export const getStudentNameCommonCell = (studentInfo, location, url) => {
    if (isEmpty(studentInfo)) {
        return { name: <TextCell classNames={`${PM_ROW_SPAN} left-border`} isDataCell={false} /> };
    }

    const { firstName, lastName, id: studentId } = studentInfo;
    const navigationUrl = getNavigationUrl(location, url, {
        studentId,
        burstGroupUid: getUrlParams(location).burstGroupUid,
    });
    const studentFullName = `${lastName} ${firstName}`;

    const name = (
        <StudentNameCell studentInfo={studentInfo} navigationUrl={navigationUrl} LinkElement={Link}>
            {/* This child is required for sorting purposes inside SpanningRowsGrid. */}
            {studentFullName}
        </StudentNameCell>
    );

    return { name };
};

export const getCompositeCell = (compositeKey, composite) => {
    let compositeCell = <TextCell classNames={PM_ROW_SPAN} isDataCell={false} />;

    if (!isEmpty(composite)) {
        const { interp, score, isSecure } = composite;
        const compositeScore = isNil(score) ? -1 : score;

        compositeCell = (
            <CompositeCell
                score={score}
                interp={interp}
                isSecure={isSecure}
                cellType={CELL_TYPE.BENCHMARK}
            >
                {compositeScore}
            </CompositeCell>
        );
    }
    return { [compositeKey]: compositeCell };
};

export const getProgressGridHeader = (
    row,
    columnStartIndex,
    columnStopIndex,
    content,
    className,
) => ({
    row,
    columnStartIndex,
    columnStopIndex,
    child: (
        <div data-tour-step={PROG_ZOG_TABLE}>
            <TextCell
                content={content}
                classNames={`table-header ${className} ${CLASS_NAME.LEFT_ALIGN}`}
                isDataCell={false}
            />
        </div>
    ),
    customStyle: COMMON_HEADER_COL_SPAN_STYLE,
});

export const getReverseThresholds = (interps, measure, grade, period) => {
    const interpByMeasureGradePeriod = getStudentResultByMeasureYearGradePeriod(
        interps,
        measure,
        null,
        grade,
        period,
    );
    return [...get(interpByMeasureGradePeriod, 'thresholds', [])].reverse();
};
export const getBenchmarkScoreCell = (
    key,
    params,
    isEmptyData,
    CommonCellBuilder,
    emptyCellClassNames,
) => ({
    [key]: isEmptyData ? (
        <TextCell classNames={emptyCellClassNames} />
    ) : (
        <CommonCellBuilder
            cellType={CELL_TYPE.BENCHMARK}
            gatingMeasureCellType={CELL_TYPE.BENCHMARK_GATING_MEASURE}
            {...params}
        />
    ),
});

export const getGoalSettingsPeriod = period => (period === BOY_MOY ? BOY_MOY : MOY_EOY);

export const getProgressPageConfig = ({
    progressTab,
    currentPeriod,
    selectedPmPeriod,
    withIntervention,
    selectedGrowthAnalysisPeriod,
    tablesContainers,
}) => {
    const { ProgressTableContainer, ZonesOfGrowthTableContainer, GrowthAnalysisTableContainer } =
        tablesContainers;

    switch (progressTab) {
        case ZOG.GOAL_SETTINGS: {
            const selectedProgressPeriod = getGoalSettingsPeriod(selectedPmPeriod);
            return {
                periods: PERIODS_FOR_ZOG,
                selectedProgressPeriod,
                currentProgressPeriod: getGoalSettingsPeriod(
                    getPmPeriodByCurrentPeriod(currentPeriod),
                ),
                helpPanelTab: 'P_ZOG',
                helpPanelTitle: getLegendPanelTitle(GOAL),
                grid: <ZonesOfGrowthTableContainer selectedPmPeriod={selectedProgressPeriod} />,
            };
        }
        case GA.GROWTH_ANALYSIS:
            return {
                periods: PERIODS_FOR_GROWTH_ANALYSIS,
                selectedProgressPeriod: selectedGrowthAnalysisPeriod,
                currentProgressPeriod: getGrowthAnalysisPeriodByCurrentPeriod(currentPeriod),
                helpPanelTab: 'P_GO',
                helpPanelTitle: getLegendPanelTitle(GROWTH_ANALYSIS),
                grid: (
                    <GrowthAnalysisTableContainer
                        selectedGrowthAnalysisPeriod={selectedGrowthAnalysisPeriod}
                    />
                ),
            };
        default:
            return {
                periods: PM_PERIODS,
                selectedProgressPeriod: selectedPmPeriod,
                currentProgressPeriod: getPmPeriodByCurrentPeriod(currentPeriod),
                helpPanelTab: HELP_PANEL_TAB,
                helpPanelTitle: getLegendPanelTitle(PROGRESS),
                grid: <ProgressTableContainer withIntervention={withIntervention} />,
            };
    }
};

export const getProgressTabs = (location, progressUrl) => {
    const params = getUrlParams(location);
    const { burstGroupUid } = params;

    return [
        {
            id: PM_TITLE,
            path: getNavigationUrl(location, progressUrl, {
                progressView: PM_TITLE,
                burstGroupUid,
            }),
            title: PM_TITLE,
        },
        {
            id: ZOG.GOAL_SETTINGS,
            path: getNavigationUrl(location, progressUrl, {
                progressView: ZOG.GOAL_SETTINGS,
                burstGroupUid,
            }),
            title: ZOG.GOAL_SETTINGS,
        },
        {
            id: GA.GROWTH_ANALYSIS,
            path: getNavigationUrl(location, progressUrl, {
                progressView: GA.GROWTH_ANALYSIS,
                burstGroupUid,
            }),
            title: GA.GROWTH_ANALYSIS,
        },
    ];
};
